import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { userEndpoint, Base_URL } from "actions/API_endpoint";
import Swal from "sweetalert2";

export default function AddEditMember() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: "",
        designation: "",
        type: "executive team",
        email: "",
        prefrence_no: 0,
    });
    const [imageFile, setImageFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            fetchMemberData();
        }
    }, [id]);

    const fetchMemberData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${Base_URL}${userEndpoint.getMemberById(id)}`);
            const data = await response.json();
            if (data.success) {
                setFormData({
                    name: data.data.name,
                    designation: data.data.designation,
                    type: data.data.type,
                    email: data.data.email || "",
                    prefrence_no: data.data.prefrence_no,
                });
                if (data.data.image_url) {
                    setPreviewImage(`${process.env.REACT_APP_API_URL || Base_URL}/uploads/${data.data.image_url}`);
                }
            } else {
                Swal.fire("Error", "Failed to fetch member details", "error");
                navigate("/admin/members");
            }
        } catch (error) {
            console.error("Error fetching member:", error);
            Swal.fire("Error", "Failed to fetch member details", "error");
            navigate("/admin/members");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const submitData = new FormData();
        submitData.append("name", formData.name);
        submitData.append("designation", formData.designation);
        submitData.append("type", formData.type);
        submitData.append("email", formData.email);
        submitData.append("prefrence_no", formData.prefrence_no);
        if (imageFile) {
            submitData.append("image", imageFile);
        }

        try {
            const url = isEditMode
                ? `${Base_URL}${userEndpoint.updateMember(id)}`
                : `${Base_URL}${userEndpoint.createMember}`;

            const method = isEditMode ? "PUT" : "POST";

            const response = await fetch(url, {
                method,
                body: submitData,
            });

            const data = await response.json();

            if (data.success) {
                Swal.fire("Success", `Member ${isEditMode ? 'updated' : 'created'} successfully!`, "success");
                navigate("/admin/members");
            } else {
                Swal.fire("Error", data.error || "Something went wrong", "error");
            }
        } catch (error) {
            console.error("Submit error:", error);
            Swal.fire("Error", "Failed to save member", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-wrap mt-4 justify-center">
            <div className="relative z-10 w-full lg:w-8/12 px-4 shadow-lg rounded bg-dark-secondary text-white py-6 mb-12">
                <div className="rounded-t bg-dark-tertiary mb-6 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <h6 className="text-white text-xl font-semibold">
                            {isEditMode ? "Edit Member" : "Add New Member"}
                        </h6>
                        <button
                            onClick={() => navigate("/admin/members")}
                            className="bg-gray-600 hover:bg-gray-500 text-white active:bg-gray-700 font-bold uppercase text-xs px-4 py-2 rounded shadow outline-none focus:outline-none ease-linear transition-all duration-150"
                        >
                            <i className="fas fa-arrow-left mr-2"></i> Back
                        </button>
                    </div>
                </div>

                {loading && isEditMode ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="px-4">
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-6/12 px-4">
                                <div className="relative w-full mb-3">
                                    <label className="block uppercase text-gray-300 text-xs font-bold mb-2">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        placeholder="Enter Name"
                                    />
                                </div>
                            </div>

                            <div className="w-full lg:w-6/12 px-4">
                                <div className="relative w-full mb-3">
                                    <label className="block uppercase text-gray-300 text-xs font-bold mb-2">
                                        Designation *
                                    </label>
                                    <input
                                        type="text"
                                        name="designation"
                                        value={formData.designation}
                                        onChange={handleChange}
                                        required
                                        className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        placeholder="E.g., Event Head"
                                    />
                                </div>
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mt-3">
                                <div className="relative w-full mb-3">
                                    <label className="block uppercase text-gray-300 text-xs font-bold mb-2">
                                        Team Type *
                                    </label>
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleChange}
                                        className="border-0 px-3 py-3 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                    >
                                        <option value="executive team">Executive Team</option>
                                        <option value="supporting team">Supporting Team</option>
                                    </select>
                                </div>
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mt-3">
                                <div className="relative w-full mb-3">
                                    <label className="block uppercase text-gray-300 text-xs font-bold mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>

                            <div className="w-full lg:w-6/12 px-4 mt-3">
                                <div className="relative w-full mb-3">
                                    <label className="block uppercase text-gray-300 text-xs font-bold mb-2">
                                        Preference No. (Sort Order) *
                                    </label>
                                    <input
                                        type="number"
                                        name="prefrence_no"
                                        value={formData.prefrence_no}
                                        onChange={handleChange}
                                        min="0"
                                        required
                                        className="border-0 px-3 py-3 placeholder-gray-500 text-white bg-dark-primary rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                    />
                                </div>
                            </div>

                            <div className="w-full px-4 mt-6">
                                <div className="relative w-full mb-3">
                                    <label className="block uppercase text-gray-300 text-xs font-bold mb-2">
                                        Member Image {isEditMode ? "" : "*"}
                                    </label>
                                    <div className="flex items-center space-x-6">
                                        <div className="shrink-0">
                                            {previewImage ? (
                                                <img
                                                    src={previewImage}
                                                    alt="Preview"
                                                    className="h-24 w-24 object-cover rounded-full border-2 border-dark-tertiary"
                                                />
                                            ) : (
                                                <div className="h-24 w-24 bg-dark-primary rounded-full flex items-center justify-center border-2 border-dark-tertiary border-dashed">
                                                    <span className="text-gray-500 text-xs">No image</span>
                                                </div>
                                            )}
                                        </div>
                                        <label className="block">
                                            <span className="sr-only">Choose file</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                required={!isEditMode && !previewImage}
                                                className="block w-full text-sm text-gray-400
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-accent-primary file:text-white
                          hover:file:bg-accent-hover cursor-pointer"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full px-4 mt-8">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="bg-accent-primary text-white active:bg-accent-hover font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 w-full ease-linear transition-all duration-150 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <span><i className="fas fa-spinner fa-spin mr-2"></i> Saving...</span>
                                    ) : (
                                        <span>Save Member</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
